import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthenticationContext.jsx';
import { LEGAL_PATH } from '../navigation/paths.js';
import { useWebSocket } from '../transport/WebSocketProvider.jsx';
import useScreen from '../hooks/useScreen.js';
import { useSession } from './SessionProvider.jsx';
import PropertySkeleton from '../screens/PropertySkeleton.jsx';

// Same deep-relative path the transport code uses for the reused socket client.
import { States } from '../../../app/src/components/WebSocketClient.js';

// Shown when the account probe failed for a reason other than "no account"
// (API error / timeout). Read-only: the only remedies are retry or sign out.
function SessionErrorOverlay({ message, onRetry, onSignOut, retrying }) {
  // This overlay can persist (a failing account probe, retried in place with no
  // route change), so it carries the same a11y wiring as a screen: the main
  // heading (id="main-content", where a closing modal returns focus if its opener
  // has gone), heading focus on arrival, and a page title.
  const headingRef = useScreen('Account error');
  return (
    <main className="app-shell">
      <h1 id="main-content" tabIndex={-1} ref={headingRef}>
        Halo
      </h1>
      {/* Role on the message, not the <main> landmark: keeps the landmark intact
          and confines the assertive announcement to static text, so toggling the
          Retry button's disabled state does not re-announce the whole region. */}
      <p className="session-gate__message" role="alert">
        {message}
      </p>
      <div className="session-gate__actions">
        <button type="button" onClick={onRetry} disabled={retrying}>
          Try again
        </button>
        <button type="button" className="btn-secondary" onClick={onSignOut}>
          Logout
        </button>
      </div>
    </main>
  );
}

// Shown when the account is missing or its accepted terms are stale. The web
// client implements neither account creation nor terms acceptance (both are
// writes), so this is purely informational: it points the user at the mobile app.
//
// It does offer the documents themselves. Asking someone to review updated terms
// without giving them anything to read is a dead end, and the same notice covers
// the no-account case, where both documents are what a new account agrees to.
// /legal is registered outside this gate precisely so this link resolves: a
// gated route would come straight back here.
function AccountSetupNotice({ onSignOut }) {
  // Persistent (no account / stale terms until resolved in the mobile app), so it
  // carries the screen a11y wiring too — see SessionErrorOverlay.
  const headingRef = useScreen('Account setup');
  return (
    <main className="app-shell">
      <h1 id="main-content" tabIndex={-1} ref={headingRef}>
        Halo
      </h1>
      <p className="session-gate__message" role="status">
        To finish setting up your account or review updated terms, please use
        the Halo mobile app.
      </p>
      <p className="session-gate__legal">
        <Link to={LEGAL_PATH} className="link-button">
          Read the Terms &amp; Conditions and Privacy Notice
        </Link>
      </p>
      <div className="session-gate__actions">
        <button type="button" className="btn-secondary" onClick={onSignOut}>
          Logout
        </button>
      </div>
    </main>
  );
}

// Gates the authenticated dashboard on the session-account probe and watches the
// live socket for a revoked session. Mounted only in App's authenticated branch,
// but every check is defensive so it is safe to render anywhere in the tree.
export default function SessionGate({ children }) {
  const { authenticated, signOut, revalidateSession } = useAuth();
  const {
    loaded,
    loading,
    fetchError,
    fetchAccount,
    shouldShowTermsAndConditions,
    shouldShowSessionError,
  } = useSession();
  const ws = useWebSocket();
  const status = ws?.webSocketInfo?.webSocketStatus;

  // Session-revocation watcher. When the socket transitions INTO Closed (retries
  // exhausted — which is what a server-side session revocation looks like from
  // here), re-check whether the Cognito session still exists. revalidateSession
  // signs the user out itself if the session is definitively gone; a transient
  // network blip leaves them signed in. A ref holds the previous status so this
  // fires once per Closed episode, not on every unrelated re-render.
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;

    if (
      status === States.Closed &&
      prevStatus !== States.Closed &&
      authenticated
    ) {
      revalidateSession();
    }
  }, [status, authenticated, revalidateSession]);

  // App's outer gate already handles the signed-out case; be defensive anyway.
  if (!authenticated) {
    return children;
  }

  // The account probe is still running. Hold the Property screen's skeleton so
  // the dashboard does not flash before we know whether the account check
  // produced an off-path state (error / setup notice) — and so the second or two
  // it takes reads as the page arriving, not a blank screen with a sentence on it.
  if (!loaded) {
    return <PropertySkeleton message="Checking your account…" />;
  }

  if (shouldShowSessionError) {
    return (
      <SessionErrorOverlay
        message={
          typeof fetchError === 'string'
            ? fetchError
            : 'Something went wrong loading your account.'
        }
        onRetry={() => fetchAccount(true)}
        onSignOut={signOut}
        retrying={loading}
      />
    );
  }

  if (shouldShowTermsAndConditions) {
    return <AccountSetupNotice onSignOut={signOut} />;
  }

  return children;
}
